<!--
  - Copyright (c) 2023. Haodong JU
  -->

<template>
  <div>
    <loading-provider :loading="loading">
      <v-row
        class="mt-4"
      >
        <v-col
          v-for="info in cardInfo"
          :key="info.id"
          cols="12"
          lg="3"
          md="6"
          sm="6"
          xl="3"
        >
          <v-card
            :color="info.color"
            :dark="info.id===4"
            class="pa-4 py-6 d-flex flex-column align-center justify-center"
            elevation="0"
            style="border-radius: 24px !important;"
          >
            <div class="text-h3 font-weight-bold">
              {{ info.value | priceDisplay }}
            </div>
            <div class="text-body-2 mt-1 font-weight-black">
              {{ info.name }}
            </div>
            <div class="text-caption grey--text">
              {{ info.desc }}
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col
          cols="12"
          lg="6"
        >
          <v-card
            class="pa-4"
            color="transparent"
            elevation="0"
            outlined
            style="border-radius: 24px !important;border: 2px solid #f0f0f0 !important;"
          >
            <daliy-win-lost-chart :info="analysisDailyInfo" />
            <div style="display: grid;grid-template-columns: repeat(2,minmax(0,1fr))">
              <div>
                <div class="text-h4">
                  {{ totalOrderNumber }}
                </div>
                <div class="text-caption">
                  {{ $t('TotalOrders') }}
                </div>
              </div>
              <div>
                <div class="text-h4">
                  {{ dailyProfit | priceDisplay }}
                </div>
                <div class="text-caption">
                  {{ $t('AverageDailyProfit') }}
                </div>
              </div>
              <div>
                <div class="text-h4">
                  {{ averageOrderProfit | priceDisplay }}
                </div>
                <div class="text-caption">
                  {{ $t('AverageOrderProfit') }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          lg="6"
        >
          <v-card
            class="pa-4"
            color="transparent"
            elevation="0"
            outlined
            style="border-radius: 24px !important;border: 2px solid #f0f0f0 !important;"
          >
            <order-type-chart
              :total-house-cost="totalHouseCost"
              :total-hydropower-cost="totalHydropowerCost"
              :total-material-cost="totalMaterialCost"
              :total-person-cost="totalDefaultPersonCost"
              :total-tax-cost="totalTaxCost"
            />
            <div style="display: grid;grid-template-columns: repeat(2,minmax(0,1fr))">
              <div>
                <div class="text-h4">
                  {{ totalAllTypeCost | priceDisplay }}
                </div>
                <div class="text-caption">
                  {{ $t('TotalCosts') }}
                </div>
              </div>
              <div>
                <div class="text-h4">
                  {{ dailyFixedCost | priceDisplay }}
                </div>
                <div class="text-caption">
                  {{ $t('AverageDailyFixedCosts') }}
                </div>
              </div>
              <div>
                <div class="text-h4">
                  {{ grossProfitMargin }}
                </div>
                <div class="text-caption">
                  {{ $t('GrossProfitMargin') }}
                </div>
              </div>
              <div>
                <div
                  v-if="needSaleToProfit < 0"
                  class="text-h4 red--text text--darken-3"
                >
                  {{ needSaleToProfit | priceDisplay }}
                </div>
                <div
                  v-else
                  class="text-h4 green--text text--darken-3"
                >
                  {{ needSaleToProfit | priceDisplay }}
                </div>
                <div class="text-caption">
                  {{ $t('AverageNetProfit') }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </loading-provider>
  </div>
</template>

<script>
import DaliyWinLostChart from '@/views/AadenPages/dashboard/fragment/chart/DaliyWinLostChart.vue'
import OrderTypeChart from '@/views/AadenPages/dashboard/fragment/chart/OrderTypeChart.vue'
import { sumBy } from 'lodash'
import { getBusinessReportInfo } from '@/model/dataLayer/api/report/report'
import { getActiveProductCode } from '@/model/dataLayer/api/SaaS/user'
import { ProductCodes } from '@/model/dataLayer/api/SaaS/product'
import { getAllCostWithOutStorage, getAllCostWithStorage } from '@/model/dataLayer/api/analysis'
import { rateDisplay } from '@/model/utils/utils'
import LoadingProvider from '@/views/widget/LoadingProvider'

export default {
  name: 'RevenueAnalysisPage',
  components: {
    LoadingProvider,
    OrderTypeChart,
    DaliyWinLostChart
  },
  props: {
    dateRange: {}
  },
  data: function () {
    return {
      loading: false,
      dailyInfo: [],
      analysisInfo: [],
      materialOutList: [],
      defaultPurchasingList: [],
      defaultFixedList: []
    }
  },
  computed: {
    cardInfo () {
      return [
        {
          id: 1,
          name: this.$t('PeriodTurnover'),
          desc: this.$t('PeriodTurnoverDesc'),
          value: this.totalTurnover,
          color: '#effcef'
        },
        {
          id: 2,
          name: this.$t('GP'),
          desc: this.$t('GrossProfitMarginDesc', { grossProfitMarginDesc: this.grossProfitMargin }),
          value: this.totalTurnover - this.totalMaterialCost,
          color: '#ffefe2'
        },
        {
          id: 3,
          name: this.$t('FixedCosts'),
          desc: this.$t('FixedCostsDesc', { fixedCostsDesc: this.fixedCostsDescVal }),
          value: parseFloat(this.totalFixedCost),
          color: '#e6f5f9'
        },
        {
          id: 4,
          name: this.$t('AccumulatedProfit'),
          desc: this.$t('AccumulatedProfitDesc',
            { accumulatedProfitDesc: this.accumulatedProfitDescVal }),
          value: this.totalTurnover - this.totalAllTypeCost,
          color: '#000000'
        }
      ]
    },
    totalAllTypeCost () {
      return (this.totalFixedCost + this.totalMaterialCost + this.totalTaxCost) || 0
    },
    totalTurnover () {
      return parseFloat(this.analysisInfo?.orderInfoInMonth?.totalTotalPrice ?? 0)
    },
    totalFixedCost () {
      return parseFloat(sumBy(this.dailyInfo, 'dailyFixed'))
    },
    totalMaterialCost () {
      return parseFloat(sumBy(this.dailyInfo, 'dailyPurchasing'))
    },
    totalOrderNumber () {
      return parseFloat(this.analysisInfo.orderInfoInMonth?.totalOrderCount ?? 0)
    },
    analysisDailyInfo () {
      return this.analysisInfo.dailyBusinessInfo.map(it => {
        const res = this.dailyInfo.find(x => x.date === it.date)
        if (!res) {
          return null
        }
        it.orderInfo.dailyCost = res?.dailyTotal ?? 0
        it.orderInfo.dailyProfit = it.orderInfo.totalTotalPrice - (res?.dailyTotal ?? 0) - it.orderInfo.totalTax
        return it
      }).filter(it => it)
    },
    dailyLength () {
      return this.analysisInfo.dailyBusinessInfo?.length ?? 0
    },
    dailyProfit () {
      return sumBy(this.analysisDailyInfo, (x) => parseFloat(x.orderInfo.totalTotalPrice)) / this.dailyLength || 0
    },
    totalTaxCost () {
      return sumBy(this.analysisDailyInfo, (x) => parseFloat(x.orderInfo.totalTax))
    },
    totalDefaultPersonCost () {
      return sumBy(this.dailyInfo, (x) => parseFloat(x.dailyPerson))
    },
    totalHouseCost () {
      return sumBy(this.dailyInfo, (x) => parseFloat(x.dailyHouse))
    },
    totalHydropowerCost () {
      return sumBy(this.dailyInfo, (x) => parseFloat(x.dailyHydropower))
    },
    averageOrderProfit () {
      if (this.totalOrderNumber === 0) {
        return 0
      } else {
        return ((this.totalTurnover - this.totalAllTypeCost) / this.totalOrderNumber).toFixed(2)
      }
    },
    grossProfitMargin () {
      const res = (this.totalTurnover - this.totalMaterialCost) / this.totalTurnover || 0
      return rateDisplay(res)
    },
    needSaleToProfit () {
      return (this.totalTurnover - this.totalAllTypeCost) / this.dailyLength
    },
    dailyFixedCost () {
      return sumBy(this.dailyInfo, 'dailyTotal') / this.dailyInfo.length
    },
    accumulatedProfitDescVal () {
      return rateDisplay(
        (this.totalTurnover - this.totalAllTypeCost) / this.totalTurnover)
    },
    fixedCostsDescVal () {
      return rateDisplay(this.totalFixedCost / this.totalTurnover)
    }
  },
  watch: {
    dateRange: {
      handler: function (val) {
        this.reload()
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    await this.reload()
  },
  methods: {
    async reload () {
      this.loading = true
      const activeProductCodeList = await getActiveProductCode()
      this.dailyInfo = activeProductCodeList.includes(ProductCodes.inventory)
        ? await getAllCostWithStorage(
          this.dateRange)
        : await getAllCostWithOutStorage(this.dateRange)
      this.analysisInfo = (await getBusinessReportInfo(this.dateRange))
      setTimeout(() => {
        this.loading = false
      }, 500)
    }
  }
}
</script>

<style scoped>

</style>
